// @flow
import type {SetDataAction, CreateAction, ClearAction} from './reservation';
import type {FetchAction} from './event';

export type AlertTypes = "error" | "warning" | "info" | "success";
export type OverlayAction = {+type: "CHECKOUT_OVERLAY", overlay: boolean};
export type RemoveAlertAction = {+type: "REMOVE_ALERT", index: number};
export type AddAlertAction = {+type: "ADD_ALERT", +message: string, +alertType: AlertTypes, +dismissable: boolean};
export type StepCompletedAction = {+type: "SET_STEP_STATUS", +stepType: string, +complete: boolean};
export type NextStepAction = {+type: "NEXT_STEP"};
export type ClearAlertAction = {+type: "CLEAR_ALERTS"};
export type ConfirmationAction = {+type: "GO_TO_CONFIRMATION"};
export type GoToStepAction = {+type: "GO_TO_STEP", +index: number};
export type SetApiUrlAction = {+type: "SET_API_URL", +url: string};
export type SetListingSlugAction = {+type: "SET_LISTING_SLUG", +string: string};
export type SetListingConfigAction = {+type: "SET_LISTING_CONFIG", +listingConfig: Object};
export type SetStepLoadingAction = {+type: "SET_STEP_LOADING", +stepLoading: boolean};

export type Action = OverlayAction | RemoveAlertAction | AddAlertAction | SetDataAction | StepCompletedAction | CreateAction | ClearAction | NextStepAction | GoToStepAction | ConfirmationAction | ClearAlertAction | SetApiUrlAction | SetListingSlugAction | FetchAction | SetListingConfigAction;

//FEE DISPLAY OPTIONS
export const TICKET_FEE_SEPARATE = 1;
export const TICKET_PRICE_ONLY = 2;

type State = {
    +overlay: boolean,
    +alerts: Array<Object>,
    +steps: Array<Object>,
    +currentStep: number,
    +confirmation: boolean,
    apiUrl: string,
    listingSlug: string,
    listingId: string,
    listingTemplate: string,
    headerImage: string,
    styleVars: string,
    uiConfig: Object,
    listingConfig: Object,
    currencyConfig: Object,
    stepLoading: boolean,
    checkoutConfig: Object,
    helpModal: boolean, // whether or not the help modal is open
    cancelConfirm: boolean, //whether or not the cancel confirm is open on kiosk
    managementOpen: boolean, // whether or not the management pane is open
    urlRouting: boolean,
    isRecurring: boolean,
    showCalendarInModal: boolean,
    sellersPreferredLocale: string,
    interactionBlock: boolean,
    collectAddressesOnFree: boolean,
    paymentHandlesSubmission: boolean,
}

let initialState = {
    overlay: false,
    alerts: [],
    steps: [],
    currentStep: 0,
    confirmation: false,
    hideCheckoutCallback: null,
    apiUrl: "",
    listingSlug: "",
    listingId: "",
    listingTemplate: "",
    headerImage: "",
    styleVars: "",
    uiConfig: {},
    listingConfig: {},
    currencyConfig: {},
    checkoutConfig: {},
    helpModal: false,
    cancelConfirm: false,
    managementOpen: false,
    urlRouting: true,
    isRecurring: false,
    listingSoldOut: false,
    showCalendarInModal: true,
    sellersPreferredLocale: "en_US",
    interactionBlock: false,
    currentMonth: null, // null | Date object. tracks the month of the series date picker calendar as the user navigates from month to month
    collectAddressesOnFree: true,
    paymentHandlesSubmission: false
}

export function ui(state: State = initialState, action: Action): State {
    if(action.type === "CHECKOUT_OVERLAY") {
        let newState = {...state};
        newState.overlay = action.overlay;

        if(action.overlay === false && typeof newState.hideCheckoutCallback === "function") {
            newState.hideCheckoutCallback();
        }

        return newState;
    } else if(action.type === "REMOVE_ALERT") {
        let newState = {...state};
        newState.alerts = [...newState.alerts];
        newState.alerts.splice(action.index, 1);
        return newState;
    } else if(action.type === "ADD_ALERT") {
        let newState = {...state};
        newState.alerts = [...newState.alerts];
        if(Array.isArray(action.message)) {
            for(let i = 0; i < action.message.length; i++) {
                if (!alertAlreadyExists(action.message[i], action.alertType, action.dismissable, newState.alerts)) {
                    newState.alerts.push({message: action.message[i], type: action.alertType, dismissable: action.dismissable});
                }
            }
        } else {
            if (!alertAlreadyExists(action.message, action.alertType, action.dismissable, newState.alerts)) {
                newState.alerts.push({message: action.message, type: action.alertType, dismissable: action.dismissable});
            }
        }
        return newState;
    } else if(action.type === "CLEAR_ALERTS") {
        let newState = {...state};
        newState.alerts = [];
        return newState;
    } else if(action.type === "TOTAL_COST_CHANGE") {
        let newState = {...state};
        let steps = [...newState.listingConfig.checkoutSteps];
        // skip this for kiosks, and probably box office in the future
        if(!newState.uiConfig.checkout || newState.uiConfig.checkout.captureMethod !== 13) {
            // check to see if there is spend for the checkout
            const amountToCheck = action && action.newTotal && action.newTotal.amount;
            // if the amount is zero or does not exist, there is no spend currently
            const isNoSpend = !amountToCheck;
            // if there is no spend, remove payment step if it exists, and add address step if the setting requires
            // if there is spend, remove address step and add payment
            const stepToAddTitle = isNoSpend ? 'Address' : 'Payment';
            const stepToRemoveTitle = isNoSpend ? 'Payment' : 'Address';
            const shouldAddStep = isNoSpend ? newState.collectAddressesOnFree : true;

            const doesNewStepAlreadyExist = steps.findIndex(step => step && step.title === stepToAddTitle) !== -1;
            const stepToRemoveIndex = steps.findIndex(step => step && step.title === stepToRemoveTitle);
            // add the step to the end of the array, if there is no step to remove
            let newStepIndex = steps.length;

            // if the step to remove exists, remove it and update the target index based on the old step's position
            if (stepToRemoveIndex !== -1) {
                steps.splice(stepToRemoveIndex, 1);
                newStepIndex = stepToRemoveIndex;
            }

            if (shouldAddStep && !doesNewStepAlreadyExist) {
                // add the new step where the old step was, or to the end of the array
                steps.splice(newStepIndex, 0, {
                    title: stepToAddTitle,
                    components: [{ name: stepToAddTitle.toLowerCase() }],
                });
            }
        }

        for(let i = 0; i < newState.currentStep; i++) {
            steps[i].complete = true;
        }

        newState.listingConfig.checkoutSteps = steps;

        return newState;
    } else if(action.type === "SET_STEP_STATUS") {
        let newState = {...state};
        newState.steps = [...newState.steps];
        for(let i = 0; i < newState.steps.length; i++) {
            if(newState.steps[i].type === action.stepType) {
                newState.steps[i].complete = action.complete;
            }
        }
        return newState;
    } else if(action.type === "NEXT_STEP") {
        let newState = {...state};
        if(newState.currentStep < newState.listingConfig.checkoutSteps.length - 1) {
            newState.currentStep++;
        }

        for(let i = 0; i < newState.steps.length; i++) {
            if(i < newState.currentStep) {
                newState.steps[i].complete = true;
            }
        }
        return newState;
    } else if(action.type === "GO_TO_STEP") {
        let newState = {...state};
        if(action.index > newState.listingConfig.checkoutSteps.length - 1) {
            return state;
        }

        newState.currentStep = action.index;
        return newState;
    } else if(action.type === "SET_STEP_COMPLETE") {
        let newState = {...state};
        newState.listingConfig = {...newState.listingConfig};
        newState.listingConfig.checkoutSteps[action.index].complete = action.value;
        return newState;
    } else if(action.type === "SET_CONFIRMATION") {
        let newState = {...state};
        newState.confirmation = action.confirmation;
        return newState;
    } else if(action.type === "GO_TO_CONFIRMATION") {
        let newState = {...state};
        newState.confirmation = true;
        newState.alerts = [];
        return newState;
    } else if(action.type === "RESERVATION_CLEARED") {
        let newState = {...state};
        newState.currentStep = 0;
        newState.currentMonth = null;
        if(action.reset) {
            newState.alerts = [];
        }
        newState.confirmation = false; // just in case it's somehow still set
        newState.showCalendarInModal = initialState.showCalendarInModal;
        if (hasCheckoutSteps(newState)) {
            newState = {
                ...newState,
                listingConfig: {
                    ...newState.listingConfig,
                    checkoutSteps: newState.listingConfig.checkoutSteps.map(step => {return {...step, complete: false}}),
                }
            };
        }
        return newState;
    } else if(action.type === "RESERVATION_COMPLETED") {
        let newState = {...state};
        newState.currentMonth = null;
        if (hasCheckoutSteps(newState)) {
            newState = {
                ...newState,
                listingConfig: {
                    ...newState.listingConfig,
                    checkoutSteps: newState.listingConfig.checkoutSteps.map(step => {return {...step, complete: false}}),
                }
            };
        }
        return newState;
    }else if(action.type === "RESERVATION_CREATED") {
        let newState = {...state};
        newState.currentStep = 0;
        newState.alerts = [];
        return newState;
    } else if(action.type === "SET_API_URL") {
        let newState = {...state};
        newState.apiUrl = action.url;
        return newState;
    } else if(action.type === "SET_LISTING_SLUG") {
        let newState = {...state};
        // in this case we were on one listing and are now loading another one
        if(action.slug !== newState.listingSlug) {
            newState.overlay = false;
        }

        newState.listingSlug = action.slug;
        return newState;
    } else if(action.type === "SET_CHECKOUT_CONFIG") {
        let newState = {...state};
        let config = Array.isArray(action.config) ? {} : action.config;

        if(newState.isRecurring && !config.seriesOptions) {
            config.seriesOptions = {calendarType: "calendar-button", showSoldOut: false}
        }

        newState.checkoutConfig = config;
        return newState;
    } else if(action.type === "SET_LISTING_CONFIG") {
        let newState = {...state};
        newState.listingConfig = action.listingConfig;

        if(action.listingConfig.styles) {
            newState.styleVars = action.listingConfig.styles;
        }

        let config = {...newState.uiConfig};
        newState.urlRouting = action.listingConfig.urlRouting;
        newState.uiConfig = Object.assign(config, action.listingConfig);

        return newState;
    } else if(action.type === "FETCH_EVENT") {
        let newState = {...state};
        newState.currencyConfig = action.data.attributes.currencyConfig;
        newState.listingId = action.data.listingId;
        newState.isRecurring = Object.values(action.data.attributes.events)[0].event.isRecurring;
        newState.listingSoldOut = action.data.attributes.listingSoldOut;
        newState.collectAddressesOnFree = action.data.attributes.settings.collectAddressesOnFree;
        return newState;
    } else if(action.type === "REFRESH_EVENT") {
        let newState = {...state};
        newState.listingSoldOut = action.data.attributes.listingSoldOut;
        return newState;
    } else if(action.type === "RESET_UI") {
        let newState = {...state};
        newState.listingConfig = initialState.listingConfig;
        newState.styleVars = initialState.styles;
        newState.checkoutConfig = {};
        newState.stepLoading = false;
        newState.interactionBlock = false;

        return newState;
    } else if(action.type === "SET_HELP_OPEN") {
        let newState = {...state};
        newState.helpModal = action.value;
        return newState;
    } else if(action.type === "SET_CANCEL_CONFIRM") {
        let newState = {...state};
        newState.cancelConfirm = action.value;
        return newState;
    } else if(action.type === "SET_MANAGEMENT_OPEN") {
        let newState = {...state};
        newState.managementOpen = action.value;
        return newState;
    } else if(action.type === "SET_DEFAULT_SHOW_CALENDAR_IN_MODAL") {
        initialState.showCalendarInModal = action.value;
        return {...state};
    } else if(action.type === "SET_SHOW_CALENDAR_IN_MODAL") {
        let newState = {...state};
        newState.showCalendarInModal = action.value;
        return newState;
    } else if(action.type === "SET_INTERACTION_BLOCK") {
        let newState = {...state};
        newState.interactionBlock = action.value;
        return newState;
    } else if(action.type === "SET_CURRENT_MONTH") {
        let newState = {...state};
        newState.currentMonth = action.value;
        return newState;
    } else if(action.type === "SET_PAYMENT_HANDLES_SUBMISSION") {
        let newState = {...state};
        newState.paymentHandlesSubmission = action.value;
        return newState;
    } else {
        return state;
    }
}

/**
 * return true if checkoutSteps exist in state
 */
const hasCheckoutSteps = state => {
    return state.listingConfig && state.listingConfig.checkoutSteps && Array.isArray(state.listingConfig.checkoutSteps);
}

const alertAlreadyExists = (message, type, dismissable, currentAlerts) => {
    for (let i = 0; i < currentAlerts.length; i++) {
        if (currentAlerts[i].message === message && currentAlerts[i].type === type && currentAlerts[i].dismissable === dismissable) {
            return true;
        }
    }
    return false;
}
